import React, { useState, useEffect, useCallback } from "react";
import {
  Route,
  Routes,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import {
  IconSmartHome,
  IconBuildingBank,
  IconFile,
  IconCalendar,
  IconCalculator,
  IconUpload,
  IconChevronDown,
  IconMenu2,
  IconLogout,
  IconPhone,
  IconEye,
  IconUsers,
  IconSearch,
  IconLoader2,
  IconUser,
  IconChevronRight,
  IconX,
} from "@tabler/icons-react";
import { Dropdown } from "antd";
import { message } from "@centrate-io/barn";
import { useApolloClient } from "@apollo/client";
import { useCustomer } from "_contexts/useCustomer";
import { usePortalSettings } from "_contexts/usePortalSettings";
import { cpAdminSearchApplications, cpAdminMasquerade } from "_graphql/mutations";
import debounce from "lodash/debounce";
import Dashboard from "./Dashboard/Dashboard";
import Accounts from "./Accounts/Accounts";
import Documents from "./Documents/Documents";
import PaymentSchedule from "./PaymentSchedule/PaymentSchedule";
import BudgetCalculator from "./BudgetCalculator/BudgetCalculator";
import TopLogo from "_assets/logos/top_logo.png";
import "./Portal.scss";

function Portal(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const client = useApolloClient();
  const { applicationId } = useParams();
  const { customer, fetchCustomer, appShortId } = useCustomer();
  const { settings: portalSettings, companyId } = usePortalSettings();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [switchUserOpen, setSwitchUserOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [applications, setApplications] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selecting, setSelecting] = useState(null);
  
  const { onLogout, onSwitchUser, isMasquerade, masqueradeAdminEmail, adminToken } = props;

  // Get logo from settings or use default
  const logoUrl = portalSettings?.logo_url || TopLogo;

  // Get company name from settings
  const companyName = portalSettings?.company?.name || "Paypath";

  // Get contact info from settings or use defaults
  const contactPhone = portalSettings?.phone_number || "(555) 123-4567";
  const contactEmail = portalSettings?.email_address || "support@paypath.com";

  // Format phone for tel: link (remove non-digits)
  const phoneLink = contactPhone.replace(/\D/g, "");

  useEffect(() => {
    if (applicationId) {
      fetchCustomer(applicationId);
    }
  }, [applicationId]);

  // Reset search when modal closes
  useEffect(() => {
    if (!switchUserOpen) {
      setSearch("");
      setApplications([]);
      setTotal(0);
    }
  }, [switchUserOpen]);

  const basePath = `/${applicationId}`;
  const currentPath = location.pathname;

  const menuItems = [
    { key: "", label: "Home", icon: IconSmartHome },
    { key: "/accounts", label: "Accounts", icon: IconBuildingBank },
    { key: "/documents", label: "Documents", icon: IconFile },
    { key: "/schedule", label: "Payment Schedule", icon: IconCalendar },
  ];

  const isActive = (key) => {
    const fullPath = `${basePath}${key}`;
    if (key === "") {
      return currentPath === basePath || currentPath === `${basePath}/`;
    }
    return currentPath === fullPath;
  };

  const supportMenuItems = [
    {
      key: "call",
      label: (
        <div className="contact-item">
          <div className="contact-details">
            <div className="contact-label">Call us</div>
            <div className="contact-value">{contactPhone}</div>
          </div>
        </div>
      ),
      onClick: () => window.open(`tel:+1${phoneLink}`),
    },
    { type: "divider" },
    {
      key: "message",
      label: (
        <div className="contact-item">
          <div className="contact-details">
            <div className="contact-label">Message us</div>
            <div className="contact-value">{contactEmail}</div>
          </div>
        </div>
      ),
      onClick: () => window.open(`mailto:${contactEmail}`),
    },
  ];

  const userMenuItems = [
    {
      key: "accountId",
      label: (
        <div className="account-id">
          <div className="account-id-text">Account ID</div>
          <div className="account-id-value">{appShortId}</div>
        </div>
      ),
    },
    { type: "divider" },
    {
      key: "logout",
      label: <span className="danger-text">Sign Out</span>,
      onClick: () => {
        if (onLogout) {
          onLogout();
        } else {
          localStorage.removeItem("JWT");
          window.location.href = "/";
        }
      },
    },
  ];

  const getInitials = () => {
    if (!customer) return "?";
    const first = customer.first_name?.[0] || "";
    const last = customer.last_name?.[0] || "";
    return (first + last).toUpperCase() || "?";
  };

  const handleUpload = () => {
    navigate(`${basePath}/documents`);
    setMobileMenuOpen(false);
  };

  // Switch user search functionality
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchApplications = useCallback(
    debounce(async (searchTerm) => {
      if (searchTerm.length < 2) {
        setApplications([]);
        setTotal(0);
        return;
      }

      if (!adminToken) {
        message.error("Admin session not found. Please exit and login again.");
        return;
      }

      setSearchLoading(true);

      try {
        const result = await client.mutate({
          mutation: cpAdminSearchApplications,
          variables: {
            token: adminToken,
            company_id: companyId,
            search: searchTerm,
            limit: 20,
          },
        });

        const searchResult = result?.data?.cpAdminSearchApplications;

        if (searchResult?.success) {
          setApplications(searchResult.applications || []);
          setTotal(searchResult.total || 0);
        } else {
          if (searchResult?.error?.includes("expired")) {
            message.error("Admin session expired. Please exit and login again.");
            setSwitchUserOpen(false);
            return;
          }
          message.error(searchResult?.error || "Search failed");
          setApplications([]);
          setTotal(0);
        }
      } catch (err) {
        console.error("Search error:", err);
        message.error("Search failed. Please try again.");
        setApplications([]);
        setTotal(0);
      } finally {
        setSearchLoading(false);
      }
    }, 300),
    [adminToken, companyId, client]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    searchApplications(value);
  };

  const handleSelectApplication = async (application) => {
    if (!adminToken) {
      message.error("Admin session not found. Please exit and login again.");
      return;
    }

    setSelecting(application.id);

    try {
      const result = await client.mutate({
        mutation: cpAdminMasquerade,
        variables: {
          token: adminToken,
          application_id: application.id,
        },
      });

      const masquerade = result?.data?.cpAdminMasquerade;

      if (masquerade?.success && masquerade?.token) {
        message.success(`Switched to ${application.first_name} ${application.last_name}`);
        setSwitchUserOpen(false);
        if (onSwitchUser) {
          onSwitchUser(masquerade.token);
        }
      } else {
        if (masquerade?.error?.includes("expired")) {
          message.error("Admin session expired. Please exit and login again.");
          setSwitchUserOpen(false);
          return;
        }
        message.error(masquerade?.error || "Failed to switch user");
        setSelecting(null);
      }
    } catch (err) {
      console.error("Switch user error:", err);
      message.error("Failed to switch user. Please try again.");
      setSelecting(null);
    }
  };

  return (
    <div className={`portal-layout ${isMasquerade ? "masquerade-mode" : ""}`}>
      {/* Masquerade Banner */}
      {isMasquerade && (
        <div className="masquerade-banner">
          <div className="masquerade-content">
            <IconEye size={18} />
            <span className="masquerade-text">
              <strong>Admin View:</strong> You are viewing{" "}
              <strong>
                {customer?.first_name} {customer?.last_name}
              </strong>
              's account
              {masqueradeAdminEmail && (
                <span className="admin-email"> (logged in as {masqueradeAdminEmail})</span>
              )}
            </span>
            <div className="masquerade-actions">
              {adminToken && (
                <button className="switch-button" onClick={() => setSwitchUserOpen(true)}>
                  <IconUsers size={14} />
                  <span>Switch</span>
                </button>
              )}
              <button className="exit-button" onClick={onLogout}>
                Exit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Switch User Modal */}
      {switchUserOpen && (
        <>
          <div className="switch-user-overlay" onClick={() => setSwitchUserOpen(false)} />
          <div className="switch-user-modal">
            <div className="modal-header">
              <h2 className="modal-title">Switch Customer</h2>
              <button className="modal-close" onClick={() => setSwitchUserOpen(false)}>
                <IconX size={20} />
              </button>
            </div>
            <div className="modal-content">
              <div className="search-input-wrapper">
                <IconSearch className="search-icon" size={18} />
                <input
                  type="text"
                  placeholder="Search by name, email, phone, or ID..."
                  value={search}
                  onChange={handleSearchChange}
                  autoFocus
                />
                {searchLoading && (
                  <IconLoader2 className="loading-icon" size={18} />
                )}
              </div>

              {/* Results List */}
              {applications.length > 0 && (
                <div className="results-section">
                  <div className="results-header">
                    <span className="results-count">
                      {total > 20 ? `Showing 20 of ${total} results` : `${total} result${total !== 1 ? "s" : ""} found`}
                    </span>
                  </div>
                  <div className="results-list">
                    {applications.map((app) => (
                      <button
                        key={app.id}
                        className={`result-item ${selecting === app.id ? "selecting" : ""}`}
                        onClick={() => handleSelectApplication(app)}
                        disabled={selecting !== null}
                      >
                        <div className="result-avatar">
                          {selecting === app.id ? (
                            <IconLoader2 className="avatar-loading" size={18} />
                          ) : (
                            <IconUser size={18} />
                          )}
                        </div>
                        <div className="result-info">
                          <span className="result-name">
                            {app.first_name} {app.last_name}
                          </span>
                          <span className="result-email">{app.email}</span>
                        </div>
                        <IconChevronRight className="result-arrow" size={18} />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* No results */}
              {search.length >= 2 && !searchLoading && applications.length === 0 && (
                <div className="no-results">
                  <p>No subscribed customers found matching "{search}"</p>
                </div>
              )}

              {/* Empty state */}
              {search.length < 2 && applications.length === 0 && (
                <div className="empty-state">
                  <IconSearch size={40} />
                  <p>Search for a customer to switch to</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Desktop Sidebar */}
      <aside className="portal-sidebar">
        <div className="sidebar-logo">
          <div className="brand-icon">
            <img src={logoUrl} alt="" />
          </div>
          <span className="brand-text">{companyName}</span>
        </div>
        <div className="sidebar-menu">
          <div className="menu-items">
            {menuItems.map((item) => (
              <button
                key={item.key}
                className={`menu-item ${isActive(item.key) ? "active" : ""}`}
                onClick={() => navigate(`${basePath}${item.key}`)}
              >
                <item.icon className="menu-item-icon" size={18} />
                <span className="menu-item-text">{item.label}</span>
              </button>
            ))}
          </div>
          <div className="sidebar-divider" />
          <div className="menu-items">
            <button
              className={`menu-item ${isActive("/budget") ? "active" : ""}`}
              onClick={() => navigate(`${basePath}/budget`)}
            >
              <IconCalculator className="menu-item-icon" size={18} />
              <span className="menu-item-text">Financial Snapshot</span>
            </button>
            <button className="upload-button" onClick={handleUpload}>
              <IconUpload className="upload-icon" size={16} />
              <span className="upload-text">Upload Documents</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="portal-main">
        {/* Desktop Header */}
        <header className="portal-header">
          <div className="header-spacer" />
          <div className="header-actions">
            <Dropdown
              menu={{ items: supportMenuItems }}
              trigger={["click"]}
              overlayClassName="desktop-support-dropdown"
            >
              <button className="contact-support-button">
                <div className="support-icon">
                  <IconPhone size={16} />
                </div>
                <span className="support-text">Contact Support</span>
                <IconChevronDown className="support-chevron" size={14} />
              </button>
            </Dropdown>
            <div className="header-divider" />
            <Dropdown
              menu={{ items: userMenuItems }}
              trigger={["click"]}
              overlayClassName="desktop-user-dropdown"
            >
              <div className="header-user">
                <div className="user-avatar">{getInitials()}</div>
                <span className="user-name">
                  {customer?.first_name} {customer?.last_name?.charAt(0)}.
                </span>
                <IconChevronDown className="user-chevron" size={14} />
              </div>
            </Dropdown>
          </div>
        </header>

        {/* Mobile Header */}
        <header className="portal-mobile-header">
          <div className="mobile-brand">
            <div className="brand-icon">
              <img src={logoUrl} alt="" />
            </div>
            <span className="brand-text">{companyName}</span>
          </div>
          <div className="mobile-actions">
            <Dropdown
              menu={{ items: supportMenuItems }}
              trigger={["click"]}
              overlayClassName="mobile-support-dropdown"
            >
              <button className="mobile-support-button">
                <IconPhone size={20} />
              </button>
            </Dropdown>
            <div className="mobile-divider" />
            <Dropdown menu={{ items: userMenuItems }} trigger={["click"]}>
              <div className="mobile-user">
                <div className="user-avatar">{getInitials()}</div>
                <IconChevronDown className="user-chevron" size={16} />
              </div>
            </Dropdown>
          </div>
        </header>

        {/* Content Area */}
        <div className="portal-content">
          <Routes>
            <Route
              path="/"
              element={<Dashboard {...props} customer={customer} />}
            />
            <Route
              path="/accounts"
              element={<Accounts {...props} customer={customer} />}
            />
            <Route
              path="/documents"
              element={<Documents {...props} customer={customer} />}
            />
            <Route
              path="/schedule"
              element={<PaymentSchedule {...props} customer={customer} />}
            />
            <Route
              path="/budget"
              element={<BudgetCalculator {...props} customer={customer} />}
            />
          </Routes>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="portal-mobile-nav">
        <button
          className={`mobile-nav-item ${isActive("") ? "active" : ""}`}
          onClick={() => setMobileMenuOpen(true)}
        >
          <IconMenu2 className="nav-item-icon" size={20} />
          <span className="nav-item-text">Menu</span>
        </button>
        <button className="mobile-nav-item" onClick={handleUpload}>
          <IconUpload className="nav-item-icon" size={20} />
          <span className="nav-item-text">Upload</span>
        </button>
      </nav>

      {/* Mobile Menu Sheet */}
      <div
        className={`mobile-menu-overlay ${mobileMenuOpen ? "open" : ""}`}
        onClick={() => setMobileMenuOpen(false)}
      />
      <div className={`mobile-menu-sheet ${mobileMenuOpen ? "open" : ""}`}>
        <div className="menu-sheet-handle" />
        <div className="menu-sheet-items">
          {menuItems.map((item) => (
            <button
              key={item.key}
              className="menu-sheet-item"
              onClick={() => {
                navigate(`${basePath}${item.key}`);
                setMobileMenuOpen(false);
              }}
            >
              <item.icon className="sheet-item-icon" size={20} />
              <span className="sheet-item-text">{item.label}</span>
            </button>
          ))}
          <div className="menu-sheet-divider" />
          <button
            className="menu-sheet-item"
            onClick={() => {
              navigate(`${basePath}/budget`);
              setMobileMenuOpen(false);
            }}
          >
            <IconCalculator className="sheet-item-icon" size={20} />
            <span className="sheet-item-text">Financial Snapshot</span>
          </button>
          <div className="menu-sheet-divider" />
          <button
            className="menu-sheet-item"
            onClick={() => {
              if (onLogout) {
                onLogout();
              } else {
                localStorage.removeItem("JWT");
                window.location.href = "/";
              }
            }}
          >
            <IconLogout className="sheet-item-icon" size={20} />
            <span className="sheet-item-text">Sign Out</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default Portal;
